import React, { useEffect, useState } from "react";
import { X, ArrowLeft, ArrowRight, Loader2, CheckCircle2, Home, FileText, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { WIZARD } from "../../content/onboardingTexts";
import { PropertyStep, TenantStep, LeaseStep } from "./WizardSteps";
import { startDemo, maybeDropDemo } from "./EmptyState";

const VIA = { headers: { "X-Via": "assistant" } };
const num = (x) => Number(String(x || "").replace(",", ".")) || 0;
const firstOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const addYears = (iso, n) => { const d = new Date(iso); d.setFullYear(d.getFullYear() + n); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); };
const errMsg = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : (d?.errors?.join(" · ") || "Une erreur est survenue"); };
const MANUAL = (s) => s === "dismissed" || s === "completed";
const save = (body) => api.put("/onboarding/wizard", body).catch(() => {});

export default function OnboardingWizard({ initial, onClose }) {
  const persist = (body) => (initial?.fresh ? Promise.resolve() : save(body));
  const nav = useNavigate();
  const [phase, setPhase] = useState(initial?.step || 0);
  const [ids, setIds] = useState(initial?.data || {});
  const [p, setP] = useState({ address: "", type: "Appartement", furnished: false, name: "", rent: "", charges: "", surface: "" });
  const [t, setT] = useState({ kind: "particulier", prenom: "", nom: "", email: "", company: "", siren: "", phone: "" });
  const [l, setL] = useState({ start: firstOfMonth(), day: "1", rent: "", charges: "", deposit: "", end: "", type: "" });
  const [names, setNames] = useState({ property: "", tenant: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.post("/onboarding/event", { event: "onboarding_started", props: { mode: "reel" } }).catch(() => {});
    if (["new", "active"].includes(initial?.status)) save({ status: "active" });
    if (ids.property_id) api.get(`/properties/${ids.property_id}`).then(({ data }) => {
      setP((x) => ({ ...x, address: data.address || "", type: data.type, furnished: data.rental_type === "Location meublée", name: data.name, rent: data.rent || "", charges: data.charges || "" }));
      setL((x) => ({ ...x, rent: x.rent || String(data.rent || ""), charges: x.charges || String(data.charges || "") }));
      setNames((n) => ({ ...n, property: data.name }));
    }).catch(() => setIds({}));
    if (ids.tenant_id) api.get(`/tenants/${ids.tenant_id}`).then(({ data }) => {
      const tt = data.tenant || data;
      setNames((n) => ({ ...n, tenant: tt.name }));
      setT((x) => ({ ...x, prenom: tt.prenom || "", nom: tt.nom || tt.name || "", email: tt.email || "" }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = (dismiss) => {
    if (phase < 4) {
      api.post("/onboarding/event", { event: "onboarding_abandoned", props: { step: String(phase) } }).catch(() => {});
      if (initial?.status) save({ status: dismiss || MANUAL(initial.status) ? (initial.status === "completed" ? "completed" : "dismissed") : "active", step: phase });
    }
    onClose();
  };

  const step1 = async () => {
    if (p.address.trim().length < 5) return setErrors({ address: "Indiquez l'adresse du bien (au moins la rue et la ville)." });
    const name = p.name.trim() || `${p.type} ${(p.street || p.address).split(",")[0]}`.slice(0, 60);
    const body = { name, type: p.type, address: p.address, city: p.city || "", postal_code: p.postal_code || "", rent: num(p.rent), charges: num(p.charges),
      surface: num(p.surface), rental_type: p.furnished ? "Location meublée" : "Location nue", rental_status: "Disponible" };
    const r = ids.property_id ? await api.put(`/properties/${ids.property_id}`, body) : await api.post("/properties", body, VIA);
    const nid = { ...ids, property_id: r.data.id || ids.property_id };
    setIds(nid); setNames((n) => ({ ...n, property: name }));
    setL((x) => ({ ...x, rent: x.rent || p.rent, charges: x.charges || p.charges, deposit: x.deposit || (num(p.rent) ? String(num(p.rent) * (p.furnished ? 2 : 1)) : "") }));
    await persist({ step: 2, data: nid });
    setPhase(2);
  };

  const step2 = async () => {
    const e = {};
    if (t.kind === "societe") { if (!t.company.trim()) e.company = "Nom de la société requis."; if (t.siren && !/^\d{9}$/.test(t.siren.replace(/\s/g, ""))) e.siren = "Le SIREN compte 9 chiffres."; }
    else { if (!t.prenom.trim()) e.prenom = "Prénom requis."; if (!t.nom.trim()) e.nom = "Nom requis."; }
    if (t.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(t.email)) e.email = "Adresse e-mail invalide.";
    if (Object.keys(e).length) return setErrors(e);
    const name = t.kind === "societe" ? t.company.trim() : `${t.prenom.trim()} ${t.nom.trim()}`;
    const body = t.kind === "societe"
      ? { name, email: t.email, phone: t.phone, property: names.property, type_locataire: "Société / Autre", company: { raison_sociale: name, siren: t.siren.replace(/\s/g, ""), siret: "" }, draft: true, force: true }
      : { name, prenom: t.prenom.trim(), nom: t.nom.trim(), email: t.email, phone: t.phone, property: names.property };
    const r = ids.tenant_id ? await api.put(`/tenants/${ids.tenant_id}`, body) : await api.post("/tenants", body, VIA);
    const nid = { ...ids, tenant_id: r.data.id || ids.tenant_id };
    setIds(nid); setNames((n) => ({ ...n, tenant: name }));
    await persist({ step: 3, data: nid });
    setPhase(3);
  };

  const step3 = async () => {
    const e = {};
    if (!l.start) e.start = "Date de début requise.";
    if (num(l.rent) <= 0) e.rent = "Indiquez le loyer hors charges.";
    if (Object.keys(e).length) return setErrors(e);
    const type = l.type || (p.furnished ? "Bail d'habitation meublé" : "Bail d'habitation vide");
    const body = { property: names.property, tenant: names.tenant, etat: "Active", type, start: l.start, end: l.end || addYears(l.start, p.furnished ? 1 : 3),
      rent: num(l.rent), charges: num(l.charges), deposit: num(l.deposit), paiement: "Mensuel", date_paiement: `${l.start.slice(0, 8)}${String(l.day).padStart(2, "0")}` };
    const r = await api.post("/leases", body, VIA);
    maybeDropDemo();
    const nid = { ...ids, lease_id: r.data.id };
    setIds(nid);
    await persist({ step: 4, ...(initial?.status ? { status: "completed" } : {}), data: nid });
    setPhase(4);
  };

  const vacant = async () => { await persist({ status: "completed", data: { ...ids, vacant: "1" } }); toast.success("Bien créé. Ajoutez le locataire quand il arrive."); onClose(); nav("/app"); };

  const next = async () => {
    setErrors({}); setBusy(true);
    try { await [step1, step2, step3][phase - 1](); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const firstReceipt = async () => {
    setBusy(true);
    try {
      await api.post(`/leases/${ids.lease_id}/generate-receipts`);
      const { data } = await api.get("/receipts");
      const list = (Array.isArray(data) ? data : data.items || []).filter((x) => x.lease_id === ids.lease_id);
      if (!list.length) { toast.info("La première quittance sera disponible à la première échéance."); onClose(); nav("/app/quittances"); return; }
      const last = list[list.length - 1];
      if (last.status !== "Payé") {
        if (!window.confirm(`Une quittance n'est émise qu'après paiement. Le loyer de ${last.month} a-t-il été payé ?`)) {
          toast.info("Votre quittance sera disponible dès que vous marquerez le loyer comme payé."); onClose(); nav("/app/quittances"); return;
        }
        await api.put(`/receipts/${last.id}/pay`, {});
      }
      const r = await api.get(`/receipts/${last.id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
      onClose(); nav("/app/quittances");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const nextDue = (() => { const d = new Date(); const day = Number(l.day) || 1; const n = new Date(d.getFullYear(), d.getMonth() + (d.getDate() > day ? 1 : 0), day); return n.toLocaleDateString("fr-FR"); })();

  return (
    <div className="fixed inset-0 z-[60] bg-brand-softer/95 backdrop-blur-sm overflow-y-auto" data-testid="onboarding-wizard">
      <div className="min-h-full max-w-xl mx-auto px-5 pt-6 pb-28 sm:pb-10 flex flex-col">
        <div className="flex items-center justify-between mb-6">
          {phase >= 1 && phase <= 3 ? (
            <div className="flex-1 mr-4" data-testid="wiz-progress">
              <p className="text-xs font-semibold text-brand-green/60 mb-1.5">Étape {phase}/3 · {WIZARD.steps[phase - 1]}</p>
              <div className="h-1.5 bg-brand-soft rounded-full overflow-hidden"><div className="h-full bg-brand-limeDark transition-all duration-500" style={{ width: `${(phase / 3) * 100}%` }} /></div>
            </div>
          ) : <span />}
          <button onClick={() => close(false)} className="p-2 rounded-full hover:bg-white text-brand-green/50" title="Fermer (reprendre plus tard)" data-testid="wiz-close"><X className="w-5 h-5" /></button>
        </div>

        {phase === 0 && (
          <div className="flex-1 flex flex-col justify-center text-center" data-testid="wiz-welcome">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-brand-lime grid place-items-center mb-5"><Sparkles className="w-8 h-8 text-brand-greenDeep" /></div>
            <h1 className="font-display text-4xl sm:text-5xl text-brand-greenDeep">{WIZARD.welcomeTitle}</h1>
            <p className="mt-3 text-lg text-brand-green/70">{WIZARD.welcomeText}</p>
            <div className="mt-8 flex flex-col gap-3">
              <button onClick={() => setPhase(1)} className="py-4 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold inline-flex items-center justify-center gap-2" data-testid="wiz-start-btn">Créer mon premier bien <ArrowRight className="w-4 h-4" /></button>
              <button onClick={() => { api.post("/onboarding/event", { event: "onboarding_started", props: { mode: "demo" } }).catch(() => {}); startDemo(); }} className="py-3 rounded-full border border-brand-soft text-brand-greenDeep text-sm hover:bg-white" data-testid="wiz-demo-btn">Explorer avec un exemple</button>
              <button onClick={() => close(true)} className="mt-2 text-sm text-brand-green/50 hover:text-brand-greenDeep underline-offset-4 hover:underline" data-testid="wiz-later-btn">Plus tard</button>
            </div>
          </div>
        )}

        {phase >= 1 && phase <= 3 && (
          <div className="flex-1">
            <h2 className="font-display text-3xl text-brand-greenDeep mb-5">{WIZARD.steps[phase - 1]}</h2>
            {phase === 1 && <PropertyStep v={p} set={(x) => { setErrors({}); setP((o) => ({ ...o, ...x })); }} errors={errors} />}
            {phase === 2 && <TenantStep v={t} set={(x) => { setErrors({}); setT((o) => ({ ...o, ...x })); }} errors={errors} propName={names.property} />}
            {phase === 3 && <LeaseStep v={l} set={(x) => { setErrors({}); setL((o) => ({ ...o, ...x })); }} errors={errors} furnished={p.furnished} />}
            {phase === 2 && <button onClick={vacant} className="mt-4 text-sm text-brand-green/60 hover:text-brand-greenDeep underline" data-testid="wiz-vacant-btn">{WIZARD.vacant}</button>}
            <div className="fixed sm:static bottom-0 inset-x-0 bg-white/95 sm:bg-transparent border-t sm:border-0 border-brand-soft p-4 sm:p-0 sm:mt-8 flex gap-3">
              <button onClick={() => { setErrors({}); if (initial?.fresh && phase === 1) onClose(); else setPhase(phase - 1); }} className="px-5 py-3.5 rounded-full border border-brand-soft text-brand-greenDeep inline-flex items-center gap-1" data-testid="wiz-back-btn"><ArrowLeft className="w-4 h-4" /> Retour</button>
              <button onClick={next} disabled={busy} className="flex-1 py-3.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="wiz-next-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {phase === 3 ? "Créer le bail" : "Continuer"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {phase === 4 && (
          <div className="flex-1 flex flex-col justify-center" data-testid="wiz-success">
            <CheckCircle2 className="w-14 h-14 text-brand-limeDark mx-auto" />
            <h2 className="mt-4 font-display text-3xl text-center text-brand-greenDeep">{initial?.fresh ? "Votre nouvelle location est prête !" : WIZARD.successTitle}</h2>
            <div className="mt-6 bg-white rounded-3xl border border-brand-soft p-5 space-y-2 text-sm" data-testid="wiz-summary">
              <p className="flex justify-between"><span className="text-brand-green/60">Bien</span><b className="text-brand-greenDeep">{names.property}</b></p>
              <p className="flex justify-between"><span className="text-brand-green/60">Locataire</span><b className="text-brand-greenDeep">{names.tenant}</b></p>
              <p className="flex justify-between"><span className="text-brand-green/60">Loyer</span><b className="text-brand-greenDeep">{num(l.rent).toLocaleString("fr-FR")} € + {num(l.charges).toLocaleString("fr-FR")} € de charges</b></p>
              <p className="flex justify-between"><span className="text-brand-green/60">Prochaine échéance</span><b className="text-brand-greenDeep">{nextDue}</b></p>
            </div>
            <p className="mt-3 text-xs text-brand-green/50 text-center">Vos nom et adresse de bailleur figurent sur les quittances : <a href="/app/profil" className="underline">compléter mon profil</a>.</p>
            <div className="mt-6 flex flex-col gap-3">
              <button onClick={firstReceipt} disabled={busy} className="py-4 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold inline-flex items-center justify-center gap-2" data-testid="wiz-first-receipt-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Générer ma première quittance</button>
              <button onClick={() => { onClose(); nav("/app"); }} className="py-3 rounded-full border border-brand-soft text-brand-greenDeep inline-flex items-center justify-center gap-2" data-testid="wiz-dashboard-btn"><Home className="w-4 h-4" /> Aller au tableau de bord</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
